/**
 * AgentsMiniGlobe.tsx
 *
 * Tiny operator-facing globe that lives at the top of the
 * `Profile → Agents` dashboard.
 *
 * Goals
 * ─────
 *   - Render a globe-projected Mapbox map showing one pin per
 *     registered agent at its last-known GeoLink position. Pins are
 *     colour-coded by status (active vs revoked) so the operator can
 *     see at a glance which fleet members are healthy. Clicking a pin
 *     selects the agent in the parent dashboard, mirroring the row
 *     list selection.
 *   - Provide a "fullscreen" mode for serious fleet management: the
 *     same map, expanded to the whole viewport, with the agent list
 *     overlayed on the side and the same click-to-select behaviour.
 *   - Stay defensive: agents with no known position are skipped from
 *     the marker layer, the bounds-fit logic falls back to a calm
 *     world view when nothing is positioned, and a missing Mapbox
 *     token is surfaced as a friendly empty state instead of a black
 *     box. The component is purely presentational — all state /
 *     mutations live in the parent.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';

import {
  getAgentPosition,
  type AgentPosition,
} from '../services/agentPositionService';
import type { CachedAgentRecord } from '../services/agentService';

interface AgentsMiniGlobeProps {
  agents: CachedAgentRecord[];
  /** Currently selected agent address — gets the pulse marker. */
  selectedAddress: string | null;
  onSelect: (address: string) => void;
  /** Compact (default) or expanded layout. */
  variant: 'compact' | 'fullscreen';
  onToggleFullscreen?: () => void;
  /**
   * Optional renderer for the side panel shown only in fullscreen
   * mode. Lets the parent thread the same agent list / detail UI it
   * shows in the docked dashboard so the fullscreen view is just a
   * larger version of the same controls.
   */
  renderSidePanel?: () => React.ReactNode;
}

interface AgentMarker {
  marker: mapboxgl.Marker;
  /** Outer element Mapbox owns and applies its translate transform to. */
  el:     HTMLDivElement;
  /**
   * Inner child we own — all visual styling (background, scale on
   * hover, selection halo) lives here. Keeping the visual transform
   * off `el` is critical: Mapbox writes `transform: translate(...)`
   * on `el` every render, so any inline `el.style.transform` we set
   * blows away the position and the marker snaps to the map's top-
   * left corner. The hover bug from "stale agent markers fly to the
   * top-left on hover" was exactly that.
   */
  inner:  HTMLDivElement;
  address: string;
}

const POSITION_REFRESH_MS = 10_000;

/**
 * Mobile-vs-desktop threshold for the fullscreen layout. Below this
 * width we switch from the side-by-side (map + side panel) grid to a
 * full-bleed map with a slide-up bottom sheet so the small viewport
 * isn't crushed into two unreadable columns.
 */
const MOBILE_BREAKPOINT_PX = 768;

/**
 * In-memory cache of the last-known position for every agent address
 * the dashboard has fetched this session. Keyed by address — survives
 * re-renders (so toggling fullscreen doesn't re-trigger N HTTP calls).
 */
const _positionCache = new Map<string, AgentPosition | null>();

/**
 * Inject the slide-up keyframes used by the mobile bottom sheet. We
 * can't put @keyframes inside a React inline style object, so we
 * lazily append a <style> tag the first time the module is evaluated
 * in a browser context. SSR-safe via the typeof guard.
 */
(function injectAgentsMiniGlobeKeyframes() {
  if (typeof document === 'undefined') return;
  const id = 'gt-agents-mini-globe-keyframes';
  if (document.getElementById(id)) return;
  const style = document.createElement('style');
  style.id = id;
  style.textContent = `
    @keyframes gt-agents-sheet-slide-up {
      from { transform: translateY(100%); }
      to   { transform: translateY(0); }
    }
  `;
  document.head.appendChild(style);
})();

/**
 * Tiny breakpoint hook — true while the viewport is narrower than
 * MOBILE_BREAKPOINT_PX. Re-evaluates on resize so rotating a phone or
 * pulling a desktop window narrow flips the fullscreen layout
 * between bottom-sheet and side-by-side without a remount.
 */
function useIsMobile(breakpoint = MOBILE_BREAKPOINT_PX): boolean {
  const [mobile, setMobile] = useState<boolean>(() =>
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false,
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = () => setMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return mobile;
}

const AgentsMiniGlobe: React.FC<AgentsMiniGlobeProps> = ({
  agents,
  selectedAddress,
  onSelect,
  variant,
  onToggleFullscreen,
  renderSidePanel,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef       = useRef<mapboxgl.Map | null>(null);
  const markersRef   = useRef<Map<string, AgentMarker>>(new Map());
  const stylesheetEnsuredRef = useRef(false);
  /**
   * When true, the next render's "auto-fly to selected agent" effect
   * will skip — used by the marker dblclick handler to keep its
   * cinematic 3D swoop from being immediately overwritten by the
   * vanilla zoom-to-selection flyTo on the very next render.
   */
  const skipNextAutoFlyRef = useRef(false);

  const isMobile = useIsMobile();
  /**
   * Mobile bottom-sheet expansion. The sheet shows the agents rail at
   * a compact height by default; selecting an agent (from the rail or
   * by tapping a marker) auto-expands it to ~75vh so the detail panel
   * is readable. Tapping the grab handle toggles the state manually.
   * Only meaningful when (variant === 'fullscreen' && isMobile).
   */
  const [sheetExpanded, setSheetExpanded] = useState(false);
  useEffect(() => {
    if (!isMobile || variant !== 'fullscreen') return;
    if (selectedAddress) setSheetExpanded(true);
  }, [isMobile, variant, selectedAddress]);

  const [positions, setPositions] = useState<Map<string, AgentPosition | null>>(() => {
    // Seed from the module-level cache so the first paint shows
    // existing pins immediately on a remount (e.g. fullscreen toggle).
    const seeded = new Map<string, AgentPosition | null>();
    agents.forEach((a) => {
      if (_positionCache.has(a.address)) seeded.set(a.address, _positionCache.get(a.address) ?? null);
    });
    return seeded;
  });

  const mapboxToken = useMemo(
    () => (mapboxgl as any).accessToken || process.env.REACT_APP_MAPBOX_TOKEN || '',
    [],
  );

  // ── Load mapbox-gl CSS lazily ─────────────────────────────────────────────
  // The rest of the app does this at boot, but we ensure it again so
  // this component can be dropped into pages that haven't already
  // imported the stylesheet (otherwise the markers would render
  // half-broken because the canvas pane styles would be missing).
  useEffect(() => {
    if (stylesheetEnsuredRef.current) return;
    if (typeof document === 'undefined') return;
    const exists = Array.from(document.styleSheets || []).some((s) => {
      try { return (s.href || '').includes('mapbox-gl'); } catch { return false; }
    });
    if (!exists) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://api.mapbox.com/mapbox-gl-js/v3.4.0/mapbox-gl.css';
      document.head.appendChild(link);
    }
    stylesheetEnsuredRef.current = true;
  }, []);

  // ── Position fetch (one round-trip per agent, with cache) ─────────────────
  useEffect(() => {
    if (agents.length === 0) return;
    let cancelled = false;
    (async () => {
      const next = new Map(positions);
      for (const a of agents) {
        if (next.has(a.address)) continue;
        try {
          const p = await getAgentPosition(a.address);
          if (cancelled) return;
          _positionCache.set(a.address, p);
          next.set(a.address, p);
        } catch {
          if (cancelled) return;
          _positionCache.set(a.address, null);
          next.set(a.address, null);
        }
      }
      if (!cancelled) setPositions(next);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agents.length, agents.map((a) => a.address).join('|')]);

  // Periodic refresh of positions for known agents.
  useEffect(() => {
    if (agents.length === 0) return;
    const id = setInterval(async () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      const next = new Map(positions);
      let touched = false;
      for (const a of agents) {
        try {
          const p = await getAgentPosition(a.address);
          _positionCache.set(a.address, p);
          if (next.get(a.address) !== p) {
            next.set(a.address, p);
            touched = true;
          }
        } catch { /* swallow — keep last-known */ }
      }
      if (touched) setPositions(next);
    }, POSITION_REFRESH_MS);
    return () => { clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agents.length, agents.map((a) => a.address).join('|')]);

  // ── Map init ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!mapboxToken || !containerRef.current || mapRef.current) return;
    mapboxgl.accessToken = mapboxToken;
    const m = new mapboxgl.Map({
      container:  containerRef.current,
      style:      'mapbox://styles/mapbox/dark-v11',
      center:     [0, 20],
      zoom:       variant === 'fullscreen' ? 1.6 : 0.8,
      projection: 'globe' as any,
      attributionControl: false,
      interactive: true,
    });

    m.on('style.load', () => {
      try { m.setFog({ color: 'rgb(15, 23, 42)', 'high-color': 'rgb(30, 41, 59)', 'horizon-blend': 0.05 } as any); } catch { /* ignore */ }
    });

    // Disable Mapbox's built-in dblclick-to-zoom so it doesn't fight
    // with our marker dblclick handler (which fires the cinematic 3D
    // swoop). The Navigation control + scroll wheel still cover normal
    // zooming, so we lose nothing.
    try { m.doubleClickZoom.disable(); } catch { /* ignore */ }

    m.addControl(new mapboxgl.NavigationControl({ showCompass: true, showZoom: true, visualizePitch: true }), 'top-right');
    mapRef.current = m;

    // Capture the marker bag at effect-init time so the cleanup
    // doesn't read `markersRef.current` after later renders may have
    // swapped it (the lint warning that flagged this is correct in
    // principle even though our ref bag is module-stable today).
    const markersBag = markersRef.current;
    return () => {
      try { m.remove(); } catch { /* ignore */ }
      mapRef.current = null;
      markersBag.clear();
    };
    // We intentionally re-create the map when `isMobile` flips inside
    // a fullscreen view: the mobile layout puts the map div at the
    // overlay's root while desktop nests it inside a column wrapper,
    // so React swaps the underlying DOM node. Without a remount the
    // mapboxgl.Map stays bound to the detached node and renders
    // nothing after the breakpoint crossing.
  }, [mapboxToken, variant, isMobile]);

  // ── Resize on layout change (e.g. flipping fullscreen) ────────────────────
  useEffect(() => {
    if (!mapRef.current || !containerRef.current) return;
    const ro = new ResizeObserver(() => {
      try { mapRef.current?.resize(); } catch { /* ignore */ }
    });
    ro.observe(containerRef.current);
    return () => { ro.disconnect(); };
  }, []);

  // ── Draw / update markers ─────────────────────────────────────────────────
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;

    const known = new Set<string>();
    for (const a of agents) {
      const p = positions.get(a.address);
      if (!p || !Number.isFinite(p.lat) || !Number.isFinite(p.lng)) continue;
      known.add(a.address);

      const isSelected = a.address === selectedAddress;
      const isRevoked  = a.status === 'revoked';
      const isStale    = !!p.is_stale;

      let entry = markersRef.current.get(a.address);
      if (!entry) {
        // Outer wrapper: Mapbox owns its `transform` (translate) so
        // we leave its inline style untouched after creation.
        const el = document.createElement('div');
        el.style.cssText = [
          'width: 18px',
          'height: 18px',
          'cursor: pointer',
        ].join(';');
        el.title = a.address;

        // Inner visual: all our styling lives here. Scaling on hover
        // does NOT touch the outer element's transform so the pin
        // stays anchored to its lng/lat.
        const inner = document.createElement('div');
        inner.style.cssText = [
          'width: 100%',
          'height: 100%',
          'border-radius: 50%',
          'border: 2px solid rgba(255,255,255,0.92)',
          'box-shadow: 0 0 0 3px rgba(15, 23, 42, 0.55)',
          'transition: transform 120ms ease',
          'transform: scale(1)',
          'box-sizing: border-box',
        ].join(';');
        el.appendChild(inner);

        el.addEventListener('mouseenter', () => { inner.style.transform = 'scale(1.18)'; });
        el.addEventListener('mouseleave', () => { inner.style.transform = 'scale(1)'; });
        el.addEventListener('click', (e) => {
          e.stopPropagation();
          onSelect(a.address);
        });
        // Double-click swoops the globe down into a 3D street-level
        // view of this agent's last-known position. We grab the
        // latest position from the cache at fire time (not closure
        // time) so a marker that has moved since render still flies
        // to the *current* lat/lng instead of a stale one.
        el.addEventListener('dblclick', (e) => {
          e.stopPropagation();
          e.preventDefault();
          const fresh = _positionCache.get(a.address) || null;
          const lat = fresh?.lat ?? p.lat;
          const lng = fresh?.lng ?? p.lng;
          // Suppress the selection-change auto-fly that the parent
          // effect would otherwise issue immediately after onSelect
          // and stomp this cinematic with a flat zoom-3 view.
          skipNextAutoFlyRef.current = true;
          onSelect(a.address);
          try {
            m.flyTo({
              center:   [lng, lat],
              zoom:     16,
              pitch:    65,
              bearing:  -25,
              speed:    0.9,
              curve:    1.6,
              duration: 2400,
              essential: true,
            });
          } catch { /* ignore — cinematic is best-effort */ }
        });
        const marker = new mapboxgl.Marker({ element: el, anchor: 'center' })
          .setLngLat([p.lng, p.lat])
          .addTo(m);
        entry = { marker, el, inner, address: a.address };
        markersRef.current.set(a.address, entry);
      } else {
        entry.marker.setLngLat([p.lng, p.lat]);
      }

      // Re-style every render so revocation / staleness updates land.
      // Note: visual styles go on `inner`, not `el`, so we don't
      // disturb Mapbox's translate transform on the outer wrapper.
      let bg = '#22c55e';
      if (isRevoked) bg = '#9ca3af';
      else if (isStale) bg = '#f59e0b';
      entry.inner.style.background = bg;
      entry.inner.style.boxShadow = isSelected
        ? `0 0 0 4px rgba(96, 165, 250, 0.95), 0 0 0 9px rgba(59, 130, 246, 0.35)`
        : '0 0 0 3px rgba(15, 23, 42, 0.55)';
      entry.el.style.zIndex = isSelected ? '5' : '1';
    }

    // Remove markers for agents that no longer have positions (or were
    // dropped from the list entirely).
    for (const [addr, entry] of Array.from(markersRef.current.entries())) {
      if (!known.has(addr)) {
        try { entry.marker.remove(); } catch { /* ignore */ }
        markersRef.current.delete(addr);
      }
    }
  }, [agents, positions, selectedAddress, onSelect]);

  // Auto-fit on first batch of positions, and re-center when the
  // selection changes (so clicking a row in the list pans the globe
  // toward its pin even if the pin was off-screen).
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    // Marker dblclick already issued a cinematic flyTo for this
    // selection — don't stomp it with a vanilla zoom-3 view.
    if (skipNextAutoFlyRef.current) {
      skipNextAutoFlyRef.current = false;
      return;
    }
    const positioned = agents
      .map((a) => ({ a, p: positions.get(a.address) }))
      .filter((row): row is { a: CachedAgentRecord; p: AgentPosition } => !!row.p && Number.isFinite(row.p.lat) && Number.isFinite(row.p.lng));

    if (positioned.length === 0) return;

    if (selectedAddress) {
      const sel = positioned.find((row) => row.a.address === selectedAddress);
      if (sel) {
        try { m.flyTo({ center: [sel.p.lng, sel.p.lat], zoom: variant === 'fullscreen' ? 4 : 3, speed: 1.2 }); } catch { /* ignore */ }
        return;
      }
    }

    // No selection — fit bounds to all positioned agents.
    if (positioned.length === 1) {
      try { m.flyTo({ center: [positioned[0].p.lng, positioned[0].p.lat], zoom: variant === 'fullscreen' ? 3 : 2, speed: 1.0 }); } catch { /* ignore */ }
      return;
    }
    const bounds = new mapboxgl.LngLatBounds();
    positioned.forEach((row) => bounds.extend([row.p.lng, row.p.lat]));
    try {
      m.fitBounds(bounds, { padding: 60, duration: 600, maxZoom: variant === 'fullscreen' ? 4 : 3 });
    } catch { /* ignore */ }
  }, [agents, positions, selectedAddress, variant]);

  // ── Render ────────────────────────────────────────────────────────────────

  const positionedCount = useMemo(() => {
    let n = 0;
    agents.forEach((a) => {
      const p = positions.get(a.address);
      if (p && Number.isFinite(p.lat) && Number.isFinite(p.lng)) n++;
    });
    return n;
  }, [agents, positions]);

  const handleEsc = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape' && variant === 'fullscreen') {
      onToggleFullscreen?.();
    }
  }, [onToggleFullscreen, variant]);

  /**
   * Bring the globe back to a flat overhead world view — used by the
   * "Reset view" button after a marker dblclick has flown the camera
   * down to a 3D street-level pitch. Also clears the selection so
   * the auto-fit-bounds path takes over again.
   */
  const handleResetView = useCallback(() => {
    const m = mapRef.current;
    if (!m) return;
    skipNextAutoFlyRef.current = true;
    try {
      m.flyTo({
        center:  [0, 20],
        zoom:    variant === 'fullscreen' ? 1.6 : 0.8,
        pitch:   0,
        bearing: 0,
        speed:   1.4,
        curve:   1.4,
        duration: 1400,
        essential: true,
      });
    } catch { /* ignore */ }
  }, [variant]);

  useEffect(() => {
    if (variant !== 'fullscreen') return;
    window.addEventListener('keydown', handleEsc);
    return () => { window.removeEventListener('keydown', handleEsc); };
  }, [variant, handleEsc]);

  if (!mapboxToken) {
    return (
      <div style={variant === 'fullscreen' ? fullscreenWrapStyle : compactWrapStyle}>
        <div style={emptyMapStyle}>
          <div style={{ fontSize: 28 }}>🌍</div>
          <div style={{ marginTop: 8, fontWeight: 600, color: '#e2e8f0' }}>Map unavailable</div>
          <div style={{ marginTop: 4, fontSize: 12, color: '#94a3b8' }}>
            <code>REACT_APP_MAPBOX_TOKEN</code> is not set on this build.
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'fullscreen') {
    // ── Mobile: full-bleed map + slide-up bottom sheet ──────────────────
    //
    // The desktop two-column grid was unusable on phones (the side
    // panel got squeezed to ~120px and the map dropped to a sliver).
    // We swap to a full-screen map with the agent list / detail panel
    // riding on a bottom sheet that mirrors the native iOS / Android
    // pattern: a thin grab handle, taps to expand / collapse, and the
    // sheet auto-expands when an agent is selected so the detail
    // panel is immediately visible.
    if (isMobile) {
      return (
        <div style={fullscreenOverlayStyle} role="dialog" aria-modal="true" aria-label="Agent fleet map">
          <div ref={containerRef} style={mobileMapStyle} />

          <div style={mobileTopBarStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              <span style={{ fontSize: 18 }}>🌍</span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 10, color: '#94a3b8', letterSpacing: 1, textTransform: 'uppercase' }}>
                  Fleet map
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#f8fafc', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {positionedCount} of {agents.length} positioned
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
              {positionedCount > 0 && (
                <button
                  type="button"
                  onClick={handleResetView}
                  style={mobileTopBarSecondaryBtnStyle}
                  title="Reset view"
                  aria-label="Reset view"
                >
                  ⌖
                </button>
              )}
              <button
                type="button"
                onClick={onToggleFullscreen}
                style={mobileTopBarCloseBtnStyle}
                title="Exit fullscreen"
                aria-label="Exit fullscreen"
              >
                ✕
              </button>
            </div>
          </div>

          <div style={mobileLegendStyle}>
            <span style={{ ...legendDot, background: '#22c55e' }} /> Active
            <span style={{ ...legendDot, background: '#f59e0b' }} /> Stale
            <span style={{ ...legendDot, background: '#9ca3af' }} /> Revoked
          </div>

          {renderSidePanel && (
            <div
              style={{
                ...mobileSheetStyle,
                height: sheetExpanded ? 'min(78vh, 620px)' : 168,
              }}
              role="dialog"
              aria-label="Agent details"
            >
              <button
                type="button"
                onClick={() => setSheetExpanded((v) => !v)}
                style={mobileSheetHandleStyle}
                aria-label={sheetExpanded ? 'Collapse details' : 'Expand details'}
                aria-expanded={sheetExpanded}
              >
                <span style={mobileSheetHandlePillStyle} />
                <span style={mobileSheetHandleLabelStyle}>
                  {sheetExpanded ? 'Tap to collapse' : (selectedAddress ? 'Tap to expand' : `${agents.length} agent${agents.length === 1 ? '' : 's'} — tap a pin or scroll`)}
                </span>
              </button>
              <div style={mobileSheetBodyStyle}>
                {renderSidePanel()}
              </div>
            </div>
          )}
        </div>
      );
    }

    // ── Desktop: existing side-by-side layout ──────────────────────────
    return (
      <div style={fullscreenOverlayStyle} role="dialog" aria-modal="true" aria-label="Agent fleet map">
        <div style={fullscreenLayoutStyle}>
          <div style={fullscreenMapColStyle}>
            <div style={fullscreenHeaderStyle}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 22 }}>🌍</span>
                <div>
                  <div style={{ fontSize: 11, color: '#94a3b8', letterSpacing: 1, textTransform: 'uppercase' }}>
                    Fleet map
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#f8fafc' }}>
                    {positionedCount} positioned · {agents.length} total
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {positionedCount > 0 && (
                  <button
                    type="button"
                    onClick={handleResetView}
                    style={fullscreenSecondaryBtnStyle}
                    title="Bring the globe back to a flat world view"
                  >
                    ⌖ Reset view
                  </button>
                )}
                <button
                  type="button"
                  onClick={onToggleFullscreen}
                  style={fullscreenCloseBtnStyle}
                  title="Exit fullscreen (Esc)"
                >
                  Exit fullscreen ✕
                </button>
              </div>
            </div>
            <div ref={containerRef} style={fullscreenMapStyle} />
            <div style={legendStyle}>
              <span style={{ ...legendDot, background: '#22c55e' }} /> Active
              <span style={{ ...legendDot, background: '#f59e0b' }} /> Stale position
              <span style={{ ...legendDot, background: '#9ca3af' }} /> Revoked
            </div>
          </div>
          {renderSidePanel && (
            <aside style={fullscreenSideStyle}>
              {renderSidePanel()}
            </aside>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={compactWrapStyle}>
      <div ref={containerRef} style={compactMapStyle} />
      <div style={compactOverlayStyle}>
        <div style={{ fontSize: 11, color: '#cbd5f5', letterSpacing: 0.5, textTransform: 'uppercase' }}>
          Fleet map
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#f8fafc' }}>
          {positionedCount} of {agents.length} positioned
        </div>
      </div>
      <div style={compactBtnRowStyle}>
        {positionedCount > 0 && (
          <button
            type="button"
            onClick={handleResetView}
            style={compactSecondaryBtnStyle}
            title="Bring the globe back to a flat world view"
          >
            ⌖ Reset view
          </button>
        )}
        {onToggleFullscreen && (
          <button
            type="button"
            onClick={onToggleFullscreen}
            style={compactFullscreenBtnStyle}
            title="Manage agents on a fullscreen globe"
          >
            ⤢ Fullscreen
          </button>
        )}
      </div>
      {agents.length === 0 && (
        <div style={compactHintStyle}>Register an agent to see it on the globe.</div>
      )}
      {agents.length > 0 && positionedCount === 0 && (
        <div style={compactHintStyle}>
          No agent positions yet — start a movement loop to plot them here.
        </div>
      )}
    </div>
  );
};

export default AgentsMiniGlobe;

// ── Styles ─────────────────────────────────────────────────────────────────

const compactWrapStyle: React.CSSProperties = {
  position: 'relative',
  width: '100%',
  height: 240,
  borderRadius: 14,
  overflow: 'hidden',
  background: '#0b1220',
  border: '1px solid #1f2937',
  boxShadow: 'inset 0 0 0 1px rgba(15, 23, 42, 0.4)',
};

const compactMapStyle: React.CSSProperties = {
  position: 'absolute', inset: 0,
};

const compactOverlayStyle: React.CSSProperties = {
  position: 'absolute',
  top: 12,
  left: 12,
  padding: '6px 12px',
  borderRadius: 10,
  background: 'rgba(15, 23, 42, 0.65)',
  backdropFilter: 'blur(6px)',
  border: '1px solid rgba(148, 163, 184, 0.25)',
  pointerEvents: 'none',
};

const compactBtnRowStyle: React.CSSProperties = {
  position: 'absolute',
  bottom: 12,
  right: 12,
  display: 'flex',
  gap: 8,
};

const compactFullscreenBtnStyle: React.CSSProperties = {
  padding: '6px 12px',
  borderRadius: 999,
  border: '1px solid rgba(148, 163, 184, 0.4)',
  background: 'rgba(15, 23, 42, 0.85)',
  color: '#f8fafc',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  letterSpacing: 0.3,
};

const compactSecondaryBtnStyle: React.CSSProperties = {
  padding: '6px 12px',
  borderRadius: 999,
  border: '1px solid rgba(148, 163, 184, 0.4)',
  background: 'rgba(15, 23, 42, 0.6)',
  color: '#cbd5f5',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  letterSpacing: 0.3,
};

const compactHintStyle: React.CSSProperties = {
  position: 'absolute',
  bottom: 12,
  left: 12,
  padding: '4px 10px',
  borderRadius: 999,
  background: 'rgba(15, 23, 42, 0.8)',
  color: '#cbd5f5',
  fontSize: 11,
  border: '1px solid rgba(148, 163, 184, 0.3)',
  pointerEvents: 'none',
};

const emptyMapStyle: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  textAlign: 'center',
  background: 'radial-gradient(circle at center, #0f172a, #020617)',
};

const fullscreenWrapStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: '#020617',
  zIndex: 9_999,
};

const fullscreenOverlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(2, 6, 23, 0.98)',
  zIndex: 9_999,
  display: 'flex',
  flexDirection: 'column',
};

const fullscreenLayoutStyle: React.CSSProperties = {
  flex: 1,
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) minmax(360px, 480px)',
  gap: 0,
  minHeight: 0,
};

const fullscreenMapColStyle: React.CSSProperties = {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
};

const fullscreenHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '14px 20px',
  borderBottom: '1px solid #1e293b',
  background: 'rgba(15, 23, 42, 0.8)',
};

const fullscreenCloseBtnStyle: React.CSSProperties = {
  padding: '8px 14px',
  borderRadius: 8,
  border: '1px solid #475569',
  background: '#1e293b',
  color: '#f8fafc',
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 600,
};

const fullscreenSecondaryBtnStyle: React.CSSProperties = {
  padding: '8px 14px',
  borderRadius: 8,
  border: '1px solid #334155',
  background: 'transparent',
  color: '#cbd5f5',
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 600,
};

const fullscreenMapStyle: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
  position: 'relative',
};

const fullscreenSideStyle: React.CSSProperties = {
  background: '#f8fafc',
  borderLeft: '1px solid #1e293b',
  overflowY: 'auto',
  padding: 16,
  minHeight: 0,
};

// ── Mobile-fullscreen styles ───────────────────────────────────────────────

const mobileMapStyle: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
};

const mobileTopBarStyle: React.CSSProperties = {
  position: 'absolute',
  top: 'env(safe-area-inset-top, 0px)',
  left: 8,
  right: 8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  padding: '8px 12px',
  marginTop: 8,
  borderRadius: 12,
  background: 'rgba(15, 23, 42, 0.78)',
  border: '1px solid rgba(148, 163, 184, 0.25)',
  backdropFilter: 'blur(8px)',
  WebkitBackdropFilter: 'blur(8px)',
  zIndex: 5,
};

const mobileTopBarSecondaryBtnStyle: React.CSSProperties = {
  width: 36,
  height: 36,
  borderRadius: 10,
  border: '1px solid rgba(148, 163, 184, 0.4)',
  background: 'rgba(15, 23, 42, 0.6)',
  color: '#cbd5f5',
  fontSize: 16,
  fontWeight: 700,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const mobileTopBarCloseBtnStyle: React.CSSProperties = {
  width: 36,
  height: 36,
  borderRadius: 10,
  border: '1px solid #475569',
  background: '#1e293b',
  color: '#f8fafc',
  fontSize: 18,
  fontWeight: 700,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const mobileLegendStyle: React.CSSProperties = {
  position: 'absolute',
  // sit just above the collapsed sheet (168px) plus a small gap
  bottom: 180,
  left: 12,
  padding: '4px 10px',
  borderRadius: 999,
  background: 'rgba(15, 23, 42, 0.85)',
  border: '1px solid rgba(148, 163, 184, 0.3)',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 11,
  color: '#cbd5f5',
  zIndex: 5,
};

const mobileSheetStyle: React.CSSProperties = {
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: 0,
  background: '#0f172a',
  borderTop: '1px solid #1e293b',
  borderTopLeftRadius: 18,
  borderTopRightRadius: 18,
  boxShadow: '0 -16px 40px rgba(0, 0, 0, 0.45)',
  display: 'flex',
  flexDirection: 'column',
  // Smooth height transition between collapsed (168px) and expanded
  // (~78vh) plus a one-shot slide-in from below on mount.
  transition: 'height 280ms cubic-bezier(0.32, 0.72, 0, 1)',
  animation: 'gt-agents-sheet-slide-up 280ms cubic-bezier(0.32, 0.72, 0, 1)',
  willChange: 'height, transform',
  zIndex: 10,
  paddingBottom: 'env(safe-area-inset-bottom, 0px)',
};

const mobileSheetHandleStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 4,
  padding: '10px 12px 8px',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  width: '100%',
  color: '#94a3b8',
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  flexShrink: 0,
};

const mobileSheetHandlePillStyle: React.CSSProperties = {
  width: 44,
  height: 4,
  borderRadius: 999,
  background: '#334155',
};

const mobileSheetHandleLabelStyle: React.CSSProperties = {
  marginTop: 2,
};

const mobileSheetBodyStyle: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  WebkitOverflowScrolling: 'touch',
  padding: 14,
  background: '#f8fafc',
  color: '#0f172a',
};

// ── Desktop-fullscreen styles ──────────────────────────────────────────────

const legendStyle: React.CSSProperties = {
  position: 'absolute',
  bottom: 12,
  left: 12,
  padding: '6px 12px',
  borderRadius: 999,
  background: 'rgba(15, 23, 42, 0.85)',
  border: '1px solid rgba(148, 163, 184, 0.3)',
  color: '#e2e8f0',
  fontSize: 11,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
};

const legendDot: React.CSSProperties = {
  display: 'inline-block',
  width: 8,
  height: 8,
  borderRadius: '50%',
  marginRight: 4,
  marginLeft: 8,
  border: '1px solid rgba(255,255,255,0.6)',
};
